import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, ArrowUp, ArrowDown, RefreshCw, PowerOff, Power } from 'lucide-react';
import { backofficeApi, formatNumber, formatDateTime, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { PromotionRuleTemplateDto, PromotionRuleTemplateRequestDto } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Table,
  TableContainer,
  Badge,
  Button,
  ActionButton,
  SearchInput,
  Select,
  TextInput,
  Switch,
  Modal,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const RuleCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;

  strong {
    font-size: 0.9rem;
    font-weight: 500;
    line-height: 1.4;
    color: ${(p) => p.theme.textPrimary};
  }
`;

/** Status toggle styled as a bordered pill, matching the Categorias screen. */
const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  color: ${(p) => p.theme.textSecondary};
`;

const Textarea = styled.textarea<{ $error?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  min-height: 110px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => (p.$error ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};
  resize: vertical;

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
  &:focus {
    outline: none;
    border-color: ${(p) => (p.$error ? p.theme.error : p.theme.primary)};
  }
`;

/** Linha sob o campo: contador à direita, mensagem de erro à esquerda. */
const FieldFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.3rem;
  font-size: 0.75rem;
`;

const ErrorText = styled.span`
  color: ${(p) => p.theme.error};
`;

const Counter = styled.span<{ $over?: boolean }>`
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$over ? p.theme.error : p.theme.textMuted)};
`;

const HelpText = styled.p`
  margin: 0.4rem 0 0;
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textMuted};
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }

  & + & {
    margin-top: 0.75rem;
  }
`;

const STATUS_FILTER = [
  { value: '', label: 'Todos os status' },
  { value: 'active', label: 'Ativas' },
  { value: 'inactive', label: 'Inativas' },
];

/** Mesmo limite do `varchar(300)` da coluna, validado aqui para não gastar um request. */
const TEXT_MAX_LENGTH = 300;

/** Friendly Portuguese copy for the business errors raised by the core. */
const SAVE_ERROR_MESSAGES: Record<string, string> = {
  PROMOTION_RULE_TEMPLATE_TEXT_EXISTS:
    'Já existe uma cláusula com esse texto no catálogo. Procure por ela na lista - se estiver inativa, reative em vez de criar outra.',
};

const EMPTY_FORM: PromotionRuleTemplateRequestDto = {
  text: '',
  appliesByDefault: false,
  active: true,
  sortOrder: 0,
};

/** Payload de edição a partir de uma cláusula existente (também usado ao reordenar). */
const toRequest = (t: PromotionRuleTemplateDto): PromotionRuleTemplateRequestDto => ({
  text: t.text,
  appliesByDefault: t.appliesByDefault,
  active: t.active,
  sortOrder: t.sortOrder,
});

const PromotionRulesPage: React.FC = () => {
  const { showNotification } = useNotification();
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<PromotionRuleTemplateRequestDto>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [statusTarget, setStatusTarget] = useState<PromotionRuleTemplateDto | null>(null);
  const [changingStatus, setChangingStatus] = useState<boolean>(false);
  const [reordering, setReordering] = useState<boolean>(false);

  const { data, loading, error, reload } = useAsync(() => backofficeApi.listPromotionRuleTemplates(), []);

  /** O core já devolve na ordem do formulário do lojista (sortOrder, depois texto). */
  const all = data ?? [];
  const term = search.trim().toLowerCase();
  const isFiltered = term !== '' || statusFilter !== '';
  const filtered = all.filter((t) => {
    const matchesText = !term || t.text.toLowerCase().includes(term);
    const matchesStatus = statusFilter === '' || (statusFilter === 'active' ? t.active : !t.active);
    return matchesText && matchesStatus;
  });

  const stats = {
    total: all.length,
    active: all.filter((t) => t.active).length,
    inactive: all.filter((t) => !t.active).length,
    /** Só conta as ativas: uma cláusula inativa marcada como padrão não chega a nenhum anúncio novo. */
    byDefault: all.filter((t) => t.active && t.appliesByDefault).length,
  };

  const trimmedText = form.text.trim();
  const textError = submitted
    ? (trimmedText === '' && 'Informe o texto da cláusula.') ||
      (trimmedText.length > TEXT_MAX_LENGTH && `A cláusula deve ter no máximo ${TEXT_MAX_LENGTH} caracteres.`) ||
      null
    : null;

  const openCreate = (): void => {
    setEditingId(null);
    // Nasce no fim da lista para não empurrar as cláusulas já revisadas para baixo.
    setForm({ ...EMPTY_FORM, sortOrder: all.length });
    setSubmitted(false);
    setIsOpen(true);
  };

  const openEdit = (template: PromotionRuleTemplateDto): void => {
    setEditingId(template.id);
    setForm(toRequest(template));
    setSubmitted(false);
    setIsOpen(true);
  };

  const update = <K extends keyof PromotionRuleTemplateRequestDto>(
    key: K,
    value: PromotionRuleTemplateRequestDto[K]
  ): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const submit = async (): Promise<void> => {
    setSubmitted(true);
    if (trimmedText === '' || trimmedText.length > TEXT_MAX_LENGTH) return;
    // O core também apara o texto; aparar aqui evita que o operador crie uma
    // "duplicata" que só difere por um espaço no fim e leve um erro sem sentido.
    const payload: PromotionRuleTemplateRequestDto = { ...form, text: trimmedText };
    setSubmitting(true);
    try {
      if (editingId) {
        await backofficeApi.updatePromotionRuleTemplate(editingId, payload);
        showNotification('Cláusula atualizada com sucesso.', 'success');
      } else {
        await backofficeApi.createPromotionRuleTemplate(payload);
        showNotification('Cláusula criada com sucesso.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar a cláusula.', SAVE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Confirma nos DOIS sentidos, e não só ao desativar.
   *
   * Reativar não é inofensivo: se a cláusula for padrão, ela volta a vir marcada
   * em todo anúncio novo a partir do clique. O texto do modal diz qual dos dois
   * efeitos vai acontecer.
   */
  const confirmStatusChange = async (): Promise<void> => {
    if (!statusTarget) return;
    const nextActive = !statusTarget.active;
    setChangingStatus(true);
    try {
      await backofficeApi.setPromotionRuleTemplateStatus(statusTarget.id, nextActive);
      showNotification(nextActive ? 'Cláusula reativada.' : 'Cláusula desativada.', 'success');
      setStatusTarget(null);
      reload();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível alterar a situação da cláusula.'),
        'error'
      );
    } finally {
      setChangingStatus(false);
    }
  };

  /**
   * Move uma cláusula uma posição para cima (-1) ou para baixo (+1).
   *
   * DECISÃO DE PRODUTO: o core não tem endpoint de reordenar em lote (o
   * `reorderCategories` das categorias não tem equivalente aqui), então mover é
   * um PUT por linha afetada. Em vez de trocar os dois `sortOrder` entre si,
   * renumeramos a lista inteira por posição e mandamos só as linhas que mudaram
   * de número. Custa o mesmo 2 requests na lista já normalizada, e resolve o
   * caso que a troca simples não resolve: `sortOrder` repetido (o core aceita, o
   * desempate é alfabético) faria a troca não mudar nada e o operador clicaria
   * a esmo. A primeira reordenação de um catálogo bagunçado pode disparar mais
   * requests; depois dela a numeração fica limpa.
   *
   * O PUT reenvia o objeto inteiro, então uma edição feita por outro operador
   * entre o carregamento da lista e o clique seria sobrescrita. É o mesmo risco
   * das outras telas de catálogo e o `reload()` no fim expõe o resultado real.
   */
  const move = async (template: PromotionRuleTemplateDto, direction: -1 | 1): Promise<void> => {
    const items = [...all];
    const index = items.findIndex((t) => t.id === template.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target], items[index]];

    const changed = items
      .map((item, position) => ({ item, position }))
      .filter(({ item, position }) => item.sortOrder !== position);
    if (changed.length === 0) return;

    setReordering(true);
    try {
      // Sequencial de propósito: são poucas linhas e um erro no meio para o
      // resto, em vez de deixar meia renumeração aplicada em paralelo.
      for (const { item, position } of changed) {
        await backofficeApi.updatePromotionRuleTemplate(item.id, { ...toRequest(item), sortOrder: position });
      }
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível reordenar as cláusulas. Confira a ordem na lista.'),
        'error'
      );
    } finally {
      setReordering(false);
      reload();
    }
  };

  return (
    <>
      <PageHeader
        title="Regras de anúncio"
        subtitle="Catálogo de cláusulas que o lojista pode marcar nos anúncios e o consumidor lê junto da oferta."
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Nova cláusula
          </Button>
        }
      />

      <StatsGrid>
        <StatCard label="Total de cláusulas" value={formatNumber(stats.total)} />
        <StatCard label="Ativas" value={formatNumber(stats.active)} />
        <StatCard label="Inativas" value={formatNumber(stats.inactive)} />
        <StatCard label="Marcadas por padrão" value={formatNumber(stats.byDefault)} />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar pelo texto da cláusula..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          options={STATUS_FILTER}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
        <ActionButton title="Atualizar" onClick={reload}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        isEmpty={filtered.length === 0}
        emptyTitle="Nenhuma cláusula encontrada"
        emptyDescription="Crie a primeira cláusula para o lojista poder marcá-la nos anúncios."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th style={{ width: '1%', whiteSpace: 'nowrap' }}>Ordem</th>
                <th>Cláusula</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Atualizada em</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((template, index) => (
                <tr key={template.id}>
                  <td style={{ width: '1%', whiteSpace: 'nowrap' }}>
                    <RowActions style={{ justifyContent: 'flex-start', gap: '0.25rem' }}>
                      {/* Com filtro ativo as setas mentiriam: a linha de cima na tela não é a vizinha real. */}
                      <ActionButton
                        aria-label="Mover para cima"
                        title={isFiltered ? 'Limpe os filtros para reordenar' : 'Mover para cima'}
                        disabled={index === 0 || reordering || isFiltered}
                        onClick={() => move(template, -1)}
                      >
                        <ArrowUp size={16} />
                      </ActionButton>
                      <ActionButton
                        aria-label="Mover para baixo"
                        title={isFiltered ? 'Limpe os filtros para reordenar' : 'Mover para baixo'}
                        disabled={index === filtered.length - 1 || reordering || isFiltered}
                        onClick={() => move(template, 1)}
                      >
                        <ArrowDown size={16} />
                      </ActionButton>
                    </RowActions>
                  </td>
                  <td>
                    <RuleCell>
                      <strong>{template.text}</strong>
                      {template.appliesByDefault && (
                        <span title="Vem marcada quando o lojista abre um anúncio novo">
                          <Badge tone="info">Padrão</Badge>
                        </span>
                      )}
                    </RuleCell>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={template.active ? 'success' : 'secondary'}>
                      {template.active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {formatDateTime(template.updatedAt ?? template.createdAt)}
                  </td>
                  <td>
                    <RowActions>
                      <ActionButton aria-label="Editar cláusula" title="Editar" onClick={() => openEdit(template)}>
                        <Edit3 size={16} />
                      </ActionButton>
                      {/* Não existe excluir: o core só desativa, para não reescrever anúncios já publicados. */}
                      <ActionButton
                        aria-label={template.active ? 'Desativar cláusula' : 'Reativar cláusula'}
                        title={template.active ? 'Desativar' : 'Reativar'}
                        color={template.active ? '#DC2626' : undefined}
                        onClick={() => setStatusTarget(template)}
                      >
                        {template.active ? <PowerOff size={16} /> : <Power size={16} />}
                      </ActionButton>
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? 'Editar cláusula' : 'Nova cláusula'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <div>
          <FieldLabel htmlFor="rule-text">Texto da cláusula</FieldLabel>
          <Textarea
            id="rule-text"
            value={form.text}
            $error={!!textError}
            maxLength={TEXT_MAX_LENGTH}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => update('text', e.target.value)}
            placeholder="Ex.: Válido apenas para consumo no local, de segunda a sexta."
          />
          <FieldFooter>
            {textError && <ErrorText>{textError}</ErrorText>}
            <Counter $over={form.text.length >= TEXT_MAX_LENGTH}>
              {form.text.length}/{TEXT_MAX_LENGTH}
            </Counter>
          </FieldFooter>
          <HelpText>
            É o texto que o consumidor lê no anúncio. Editar aqui reescreve a cláusula em todos os anúncios
            que a utilizam - corrija a redação à vontade, mas se o sentido mudar, crie outra cláusula e
            desative esta.
          </HelpText>
        </div>

        <TextInput
          label="Ordem"
          type="number"
          value={String(form.sortOrder)}
          onChange={(e) => update('sortOrder', Number(e.target.value) || 0)}
          containerStyle={{ marginTop: '0.75rem' }}
        />
        <HelpText>Define a posição da cláusula na lista do formulário do lojista. Menor número aparece primeiro.</HelpText>

        <ToggleField>
          <Switch checked={form.appliesByDefault} onChange={(v) => update('appliesByDefault', v)} />
          Marcada por padrão
        </ToggleField>
        <HelpText>
          Quando ligada, a cláusula já vem marcada ao lojista abrir um anúncio novo. Ele pode desmarcar, e
          anúncios que já existem não são afetados.
        </HelpText>

        <ToggleField>
          <Switch checked={form.active} onChange={(v) => update('active', v)} />
          Ativa
        </ToggleField>
        <HelpText>
          Cláusula inativa sai do formulário do lojista, mas continua sendo exibida nos anúncios que já
          apontam para ela.
        </HelpText>
      </Modal>

      <Modal
        isOpen={statusTarget !== null}
        onClose={() => setStatusTarget(null)}
        title={statusTarget?.active ? 'Desativar cláusula' : 'Reativar cláusula'}
        width="460px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setStatusTarget(null)} disabled={changingStatus}>
              Cancelar
            </Button>
            <Button
              variant={statusTarget?.active ? 'danger' : 'primary'}
              onClick={confirmStatusChange}
              isLoading={changingStatus}
            >
              {statusTarget?.active ? 'Desativar' : 'Reativar'}
            </Button>
          </>
        }
      >
        {statusTarget?.active ? (
          <>
            <ConfirmText>
              Desativar a cláusula <strong>{statusTarget?.text}</strong>?
            </ConfirmText>
            <ConfirmText>
              Ela deixa de aparecer no formulário do lojista. Os anúncios que já a utilizam continuam
              exibindo o texto normalmente - nada é excluído, e você pode reativá-la a qualquer momento.
            </ConfirmText>
          </>
        ) : (
          <>
            <ConfirmText>
              Reativar a cláusula <strong>{statusTarget?.text}</strong>?
            </ConfirmText>
            <ConfirmText>
              Ela volta a aparecer no formulário do lojista
              {statusTarget?.appliesByDefault ? ', já marcada por padrão em todo anúncio novo.' : '.'}
            </ConfirmText>
          </>
        )}
      </Modal>
    </>
  );
};

export default PromotionRulesPage;
